import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaTrash, FaEye, FaPen } from "react-icons/fa";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000BasicDialog from "../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000YesNoDialog from "../../../u2000Core/components/dialog/U2000YesNoDialog";
import {
  useU3142GetDocumentsHandlers,
  useU3142DeleteDocumentHandlers,
  useU3142PatchDocumentNameHandlers,
  useU3142UseViewDocumentHandlers,
} from "./U3142UseDocumentsHandlers";
import { U3142AddDocumentDialog } from "./U3142AddDocumentDialog";
import {
  doViewDocument,
  luHandleAppiError,
  U3142_LOT_DETAIL_DOCUMENT_TYPES,
  type U3142LotDetailDocument,
  type U3142LotDetail,
} from "../../U3142Service";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { NS } from "../../U3142";

type U3142DocumentRowIconActionProps = {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
};

function U3142DocumentRowIconAction({
  label,
  icon: Icon,
  onClick,
  disabled,
  destructive = false,
}: U3142DocumentRowIconActionProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 ${
        destructive ? "text-red-600 hover:bg-red-50" : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

export interface U3142LotDocumentsTabProps {
  lotId: string | number;
  /** Données du lot (pour refetch parent). */
  lotDetail: U3142LotDetail | null;
  /** Callback après ajout/suppression pour rafraîchir le formulaire parent. */
  onDocumentsChange?: () => void;
  /** Désactive les boutons Ajouter et Supprimer lorsque true (ex. formulaire en mode édition : on désactive pour laisser l’utilisateur sauvegarder le lot d’abord). En mode lecture, les actions sont actives. */
  disableDocumentActions?: boolean;
}

export const U3142LotDocumentsTab: React.FC<U3142LotDocumentsTabProps> = ({
  lotId,
  lotDetail,
  onDocumentsChange,
  disableDocumentActions = false,
}) => {
  const { t } = useTranslation();
  const axios = useU2000AxiosAuthenticated();
  const toast = useToast();
  const [documents, setDocuments] = useState<U3142LotDetailDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ doc: U3142LotDetailDocument } | null>(null);
  const [renameDoc, setRenameDoc] = useState<U3142LotDetailDocument | null>(null);
  const [renameNameDraft, setRenameNameDraft] = useState("");

  const refetch = useCallback(() => {
    onDocumentsChange?.();
  }, [onDocumentsChange]);

  const { loadDocuments } = useU3142GetDocumentsHandlers({
    lotId,
    onSuccess: setDocuments,
  });

  const onDeleteSuccess = useCallback(() => {
    loadDocuments().then(setDocuments);
    refetch();
  }, [loadDocuments, refetch]);

  const { deleteDocument } = useU3142DeleteDocumentHandlers({
    lotId,
    onSuccess: onDeleteSuccess,
  });

  const onRenameSuccess = useCallback(() => {
    loadDocuments().then(setDocuments);
    refetch();
  }, [loadDocuments, refetch]);

  const { patchDocumentName } = useU3142PatchDocumentNameHandlers({
    lotId,
    onSuccess: onRenameSuccess,
  });

  const { viewDocument } = useU3142UseViewDocumentHandlers({
    doViewDocumentFn: (documentId) => doViewDocument(axios, documentId),
    onError: (error) => {
      const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (err?.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
    },
  });

  useEffect(() => {
    if (!lotId) return;
    setLoading(true);
    loadDocuments()
      .then(setDocuments)
      .finally(() => setLoading(false));
  }, [lotId, loadDocuments]);

  useEffect(() => {
    if (renameDoc) {
      setRenameNameDraft(renameDoc.documentName ?? "");
    } else {
      setRenameNameDraft("");
    }
  }, [renameDoc]);

  const handleViewDocument = (documentId: string | number) => {
    viewDocument(documentId);
  };

  const handleDeleteConfirm = (result: "yes" | "no") => {
    if (result === "yes" && deleteConfirm) {
      deleteDocument(deleteConfirm.doc.id);
    }
    setDeleteConfirm(null);
  };

  const handleRenameSave = () => {
    const trimmed = renameNameDraft.trim();
    if (!trimmed) {
      toast.error(t("errRenameDocumentNameRequired", { ns: NS }), t("errorTitle", { ns: NS }));
      return;
    }
    if (!renameDoc) return;
    void patchDocumentName(renameDoc.id, trimmed).then(() => {
      setRenameDoc(null);
    });
  };

  const getDocumentTypeLabel = (documentType?: string) => {
    if (!documentType) return "—";
    const key = `lblDocumentType.${documentType}`;
    const label = t(key, { ns: NS });
    return label !== key ? label : documentType;
  };

  const canAddDocument = U3142_LOT_DETAIL_DOCUMENT_TYPES.length > 0;

  const tableBlock = loading ? (
    <p className="text-sm text-gray-500">{t("msgLoading", { ns: NS })}</p>
  ) : documents.length === 0 ? (
    <p className="text-sm text-gray-500">{t("msgNoDocuments", { ns: NS })}</p>
  ) : (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("lblDocumentType", { ns: NS })}
            </th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("lblDocumentName", { ns: NS })}
            </th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("lblProjectNumber", { ns: NS })}
            </th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("lblActions", { ns: NS })}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {documents.map((doc) => (
            <tr key={String(doc.id)} className="hover:bg-gray-50/80">
              <td className="px-4 py-3 text-sm font-medium text-gray-800">{getDocumentTypeLabel(doc.documentType)}</td>
              <td
                className="max-w-[240px] truncate px-4 py-3 text-sm text-gray-600"
                title={doc.documentName ?? undefined}
              >
                {doc.documentName ?? "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {doc.projectNumber ?? doc.project?.projectNumber ?? "—"}
              </td>
              <td className="px-4 py-2 text-right">
                <div className="flex justify-end gap-0.5">
                  <U3142DocumentRowIconAction
                    label={t("btnViewDocument", { ns: NS })}
                    icon={FaEye}
                    onClick={() => handleViewDocument(doc.id)}
                  />
                  <U3142DocumentRowIconAction
                    label={t("btnRenameDocument", { ns: NS })}
                    icon={FaPen}
                    onClick={() => setRenameDoc(doc)}
                    disabled={disableDocumentActions}
                  />
                  <U3142DocumentRowIconAction
                    label={t("btnDelete", { ns: NS })}
                    icon={FaTrash}
                    onClick={() => setDeleteConfirm({ doc })}
                    disabled={disableDocumentActions}
                    destructive
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="flex flex-col">
        <div className="flex items-center justify-end border-b border-gray-100 bg-gray-50 px-4 py-3">
          <U2000ActionBtn
            label={t("btnAddDocument", { ns: NS })}
            icon={FaPlus}
            handleClick={() => setAddDialogOpen(true)}
            disabled={disableDocumentActions || !canAddDocument}
          />
        </div>

        <div className="p-4">{tableBlock}</div>
      </div>

      <U3142AddDocumentDialog
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={() => {
          loadDocuments().then(setDocuments);
          refetch();
        }}
        lotId={lotId}
        existingDocuments={documents}
      />

      <U2000YesNoDialog
        isOpen={!!deleteConfirm}
        onClose={handleDeleteConfirm}
        title={t("dlgDeleteDocumentTitle", { ns: NS })}
      >
        <p>{t("msgDeleteDocumentConfirm", { ns: NS })}</p>
      </U2000YesNoDialog>

      <U2000BasicDialog
        isOpen={!!renameDoc}
        onClose={() => setRenameDoc(null)}
        title={t("dlgRenameDocumentTitle", { ns: NS })}
        width="medium"
      >
        <div className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="u3142-rename-document-name" className="mb-1 block text-sm font-medium text-gray-700">
              {t("lblDocumentName", { ns: NS })}
            </label>
            <input
              id="u3142-rename-document-name"
              type="text"
              autoComplete="off"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              value={renameNameDraft}
              onChange={(e) => setRenameNameDraft(e.target.value)}
            />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50"
              onClick={() => setRenameDoc(null)}
            >
              {t("dlgLblCancel", { ns: NS })}
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
              onClick={() => void handleRenameSave()}
            >
              {t("btnSaveRenameDocument", { ns: NS })}
            </button>
          </div>
        </div>
      </U2000BasicDialog>

    </>
  );
};
